import { useCallback, useEffect, useRef } from 'react';

type Options = {
  audioFileUrl: string;
};

type Result = {
  playSound: () => void;
};

const useNotificationSound = ({ audioFileUrl }: Options): Result => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);

  useEffect(() => {
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContext) {
      console.error('AudioContext is not supported in this environment');
      return;
    }

    const audioContext = new AudioContext();
    const request = new XMLHttpRequest();
    request.open('GET', audioFileUrl, true);
    request.responseType = 'arraybuffer';

    request.onload = () => {
      audioContext.decodeAudioData(request.response, (buffer) => {
        audioBufferRef.current = buffer;
      }, (error) => {
        console.error('Failed to decode audio data:', error);
      });
    };

    request.onerror = (error) => {
      console.error('Failed to load audio file:', error);
    };

    request.send();

    audioContextRef.current = audioContext;

    // Cleanup: Close the audio context when the component unmounts.
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [audioFileUrl]);

  const playSound = useCallback(() => {
    if (!audioContextRef.current || !audioBufferRef.current) {
      console.error('Audio context or audio buffer is not available');
      return;
    }

    const audioSource = audioContextRef.current.createBufferSource();
    audioSource.buffer = audioBufferRef.current;
    audioSource.connect(audioContextRef.current.destination);
    audioSource.start(0); 
  }, []);

  return {
    playSound,
  };
};

export default useNotificationSound;
